import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Button, IconButton, Tooltip} from '@primer/react'
import {useRef} from 'react'

import {AlertsSidePanel} from './AlertsSidePanel'
import {CounterSkeleton} from './CounterSkeleton'
import {useAnnotations, usePullRequestBasePath} from '../../stores/selectors'
import {useAnnotationsCount} from '../../stores/layout-selectors'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'

import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface OpenAlertsPanelButtonProps {
  pageLimits?: PageLimits
  endCommitOid: string
  className?: string
  hideIfNoAlerts?: boolean
}

/**
 *
 * Renders a button that opens the alerts panel
 * Contains the total number of alerts plus the icon
 */
export function OpenAlertsPanelButton({
  pageLimits,
  endCommitOid,
  className,
  hideIfNoAlerts,
}: OpenAlertsPanelButtonProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Alerts)

  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()

  const alerts = useAnnotations()
  const storeAnnotationsCount = useAnnotationsCount()
  const basePath = usePullRequestBasePath()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const showUpdatedHeader = isFeatureEnabled('updated_pull_request_header')
  const totalAlertsCount = showUpdatedHeader ? storeAnnotationsCount : alerts.length
  const isLoading = showUpdatedHeader && storeAnnotationsCount === null
  const isSidePanelOpen = isDockedPanelOpen
  const alertsButtonAriaLabel = isDockedPanelOpen ? 'Close alerts panel' : 'Open alerts panel'

  // TODO: Remove this when permanently removing this button from the PullRequestFilesToolbar as part of graduating `updated_pull_request_header`
  if (hideIfNoAlerts && totalAlertsCount === 0) {
    return (
      <ErrorBoundary boundaryName="OpenAlertsPanelButton" fallback={null}>
        <AlertsSidePanel
          annotations={alerts}
          isOpen={isDockedPanelOpen}
          pageLimits={pageLimits}
          pathName={basePath}
          endCommitOid={endCommitOid}
          returnFocusRef={toggleSidePanelRef}
          onClose={() => actions.closePanel()}
        />
      </ErrorBoundary>
    )
  }

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenAlertsPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The alerts panel cannot currently be opened."
            icon={AlertIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Tooltip ref={toggleSidePanelRef} text={alertsButtonAriaLabel} direction="sw" type="label">
          <Button
            className={className}
            aria-expanded={isSidePanelOpen}
            count={isLoading ? undefined : (totalAlertsCount ?? undefined)}
            leadingVisual={AlertIcon}
            trailingVisual={isLoading ? CounterSkeleton : undefined}
            size="small"
            variant="invisible"
            onClick={() => {
              sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
              actions.togglePanel('alerts', toggleSidePanelRef.current)
            }}
          />
        </Tooltip>
        <AlertsSidePanel
          annotations={alerts}
          isOpen={isDockedPanelOpen}
          pageLimits={pageLimits}
          pathName={basePath}
          endCommitOid={endCommitOid}
          returnFocusRef={toggleSidePanelRef}
          onClose={actions.closePanel}
        />
      </ErrorBoundary>
    </div>
  )
}
